import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CardContainer,
  CardBody,
  CardItem,
} from "../../components/ui/cardthreedimension";
import ResponsiveImage from "../../components/elements/ResponsiveImage/ResponsiveImage";
import SanityImage from "../../components/elements/SanityImage/SanityImage";
import { IMAGE_SIZES } from "../../components/elements/ResponsiveImage/imageSizes";
import type { SanityImageData } from "@/types/sanity";
import type { LocalImageVariants } from "@/lib/localProjects";

interface ProjectCardProps {
  id?: string;
  slug?: string;
  /** Static image URL (local projects). Ignored when `image` is provided. */
  imageUrl?: string;
  /** Thumbnails for a local image; when present the card loads a small copy instead of `imageUrl`. */
  variants?: LocalImageVariants;
  /** Sanity image; rendered responsively through the image pipeline. */
  image?: SanityImageData | null;
  title: string;
  category?: string;
  /** Load this card's image straight away (use for the first cards, which are on screen). */
  priority?: boolean;
}

const IMAGE_CLASS = "block w-full h-full object-cover cursor-pointer";

const cardItemVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1.02,
    transition: {
      // Quick enough that the image, not the animation, is what the visitor waits for.
      duration: 0.35,
      ease: "easeOut",
    },
  },
};

const hoverVariants = {
  scale: 1.05,
  boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.15)",
  transition: {
    type: "spring",
    stiffness: 300,
    damping: 20,
    duration: 0.4,
  },
};

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  slug,
  imageUrl,
  variants,
  image,
  title,
  priority = false,
}) => {
  // A real link, so visitors can open it in a new tab and search engines can follow it.
  const target = slug ?? id;
  const altText = `${title} waterproofing project`;

  const card = (
    <CardContainer
      className="w-full my-1.5 cursor-pointer"
      containerClassName="w-full cursor-pointer"
    >
      <motion.div
        variants={cardItemVariants}
        initial="hidden"
        animate="visible"
        whileHover={hoverVariants}
        className="w-full h-full flex flex-col cursor-pointer"
      >
        <CardBody
          className="bg-white rounded-lg shadow-md overflow-hidden w-full h-auto flex flex-col cursor-pointer"
        >
          <CardItem translateZ={5} className="flex-shrink-0 cursor-pointer">
            {/* The grey backdrop shows while the image is on its way, instead of a blank white box. */}
            <div className="w-full aspect-[4/3] overflow-hidden rounded-t-lg cursor-pointer bg-slate-200">
              {image ? (
                <SanityImage
                  image={image}
                  alt={altText}
                  sizes={IMAGE_SIZES.projectCard}
                  className={IMAGE_CLASS}
                  draggable={false}
                  priority={priority}
                />
              ) : variants ? (
                <ResponsiveImage
                  src={variants.src}
                  srcSet={variants.srcSet}
                  sizes={IMAGE_SIZES.projectCard}
                  width={variants.width}
                  height={variants.height}
                  lqip={variants.lqip}
                  alt={altText}
                  className={IMAGE_CLASS}
                  draggable={false}
                  priority={priority}
                />
              ) : (
                <ResponsiveImage
                  src={imageUrl ?? ""}
                  alt={altText}
                  className={IMAGE_CLASS}
                  draggable={false}
                  priority={priority}
                />
              )}
            </div>
          </CardItem>
          <CardItem
            translateZ={5}
            className="p-4 text-center font-bold content-text-size text-gray-800 flex-1 flex items-center justify-center cursor-pointer"
          >
            {title}
          </CardItem>
        </CardBody>
      </motion.div>
    </CardContainer>
  );

  return target ? (
    <Link to={`/projects/${target}`} className="block">
      {card}
    </Link>
  ) : (
    card
  );
};

export default ProjectCard;
